﻿<script setup lang="ts">
import { ref, computed, watch, nextTick } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import axios from 'axios';
import axiosInstance from '@/services/interceptor.services';
import { VITE_API_URL } from '@/utils/runtimeConfig';
import { TranslationService } from '@/services/translation.services';
import { token } from '@/refs/common.refs';
import { organizationIdCommerce } from '@/refs/commerce.refs';
import { useQuery, useQueryClient } from '@tanstack/vue-query';
import ProductEditTabs from '@/components/product/edit/ProductEditTabs.vue';
import ProductGeneralTab from '@/components/product/edit/ProductGeneralTab.vue';
import ProductPricingTab from '@/components/product/edit/ProductPricingTab.vue';
import ProductVariantsTab from '@/components/product/edit/ProductVariantsTab.vue';
import ProductAddonGroupsTab from '@/components/product/edit/ProductAddonGroupsTab.vue';
import ProductAllergensTab from '@/components/product/edit/ProductAllergensTab.vue';
import ProductAvailabilityTab from '@/components/product/edit/ProductAvailabilityTab.vue';
import type { TabKey } from '@/components/product/edit/edit-tabs.types';

const { t } = useI18n({ useScope: 'global' });

/* ── Onglets ────────────────────────────────────────────────────────────── */
const activeTab = ref<TabKey>('general');
const TAB_LIST = computed(() => [
  { key: 'general', label: t('product.catDesignation') },
  { key: 'pricing', label: t('product.priceAndType') },
  { key: 'variants', label: t('product.variantsOptional') },
  { key: 'addonGroups', label: t('product.addonGroupsOptional') },
  { key: 'allergens', label: 'Allergènes' },
  { key: 'availability', label: 'Disponibilité' },
]);

const router = useRouter();
const route = useRoute();
const queryClient = useQueryClient();
const id = computed(() => route.params.id as string);
const fromId = computed(() => route.query.from as string | undefined);
const fromTab = computed(() => route.query.tab as string | undefined);
const fromCatTab = computed(() => route.query.catTab as string | undefined);

function goBack() {
  if (fromId.value) {
    const params = new URLSearchParams();
    if (fromTab.value) params.set('tab', fromTab.value);
    if (fromCatTab.value) params.set('catTab', fromCatTab.value);
    const qs = params.toString();
    router.push(`/commerces/${fromId.value}${qs ? `?${qs}` : ''}`);
  } else router.push(`/products/${id.value}`);
}

/* ── Pagination produits du même commerce ───────────────────────────────── */
const { data: storeProducts } = useQuery({
  queryKey: computed(() => ['store-products-list', fromId.value]),
  queryFn: async () => {
    const params = new URLSearchParams({
      restau: fromId.value!,
      pagination: 'false',
    });
    const r = await axiosInstance.get(`products/search?${params}`);
    const list: any[] =
      (r.data as any)?.data?.data ??
      (r.data as any)?.data ??
      (r.data as any) ??
      [];
    return list;
  },
  enabled: computed(() => !!fromId.value),
});

const currentIndex = computed(() => {
  const list = storeProducts.value;
  if (!list?.length) return -1;
  return list.findIndex((p: any) => p.id === id.value);
});

const prevProduct = computed(() => {
  const list = storeProducts.value;
  if (!list?.length || currentIndex.value <= 0) return null;
  return list[currentIndex.value - 1];
});

const nextProduct = computed(() => {
  const list = storeProducts.value;
  if (
    !list?.length ||
    currentIndex.value < 0 ||
    currentIndex.value >= list.length - 1
  )
    return null;
  return list[currentIndex.value + 1];
});

function goToProduct(productId: string) {
  if (!fromId.value) {
    router.push(`/products/${productId}/edit`);
    return;
  }
  const params = new URLSearchParams({ from: fromId.value });
  if (fromTab.value) params.set('tab', fromTab.value);
  if (fromCatTab.value) params.set('catTab', fromCatTab.value);
  router.push(`/products/${productId}/edit?${params}`);
}

/* ── Chargement du produit ──────────────────────────────────────────────── */
const {
  data: product,
  isPending: loadingProduct,
  isError: loadError,
} = useQuery({
  queryKey: computed(() => ['product-detail', id.value]),
  queryFn: async () => {
    const r = await axiosInstance.get(`products/get/${id.value}`);
    const d = r.data as any;
    return d?.data ?? d;
  },
  enabled: computed(() => !!id.value),
});

// Les groupes d'options (extras) ne sont pas inclus dans products/get/{id} —
// route dédiée, comme dans CommerceDetailPage.vue.
const { data: productExtras } = useQuery({
  queryKey: computed(() => ['product-extras', id.value]),
  queryFn: async () => {
    const r = await axiosInstance.get(`product/${id.value}/extras`);
    const d = r.data as any;
    return (d?.data ?? d ?? []) as any[];
  },
  enabled: computed(() => !!id.value),
});

/* ── Traduction (backend interne) ────────────────────────────────────────── */
async function translateAll(
  src: 'fr' | 'en' | 'nl',
  get: () => string,
  set: (l: 'fr' | 'en' | 'nl', v: string) => void,
  busy: { value: boolean },
) {
  const text = get();
  if (!text.trim() || busy.value) return;
  busy.value = true;
  try {
    const translated = await TranslationService.translateText(text, src);
    (['fr', 'en', 'nl'] as const)
      .filter((l) => l !== src)
      .forEach((l) => set(l, translated[l]));
  } finally {
    busy.value = false;
  }
}

/* ── Catégories ─────────────────────────────────────────────────────────── */
type Category = {
  id: string;
  designation: any;
  description?: any;
  modules?: string[];
};
const categories = ref<Category[]>([]);
const loadingCats = ref(false);

function catLabel(c: Category): string {
  const d = c.designation;
  if (!d) return '—';
  if (typeof d === 'string') return d;
  return d.fr || d.en || d.nl || '—';
}

async function fetchCategories() {
  const orgId = organizationIdCommerce.value;
  if (!orgId) return;
  loadingCats.value = true;
  try {
    const r = await axiosInstance.get(
      `products/categories/search?pagination=false&organizationIds=${orgId}`,
    );
    const d = r.data as any;
    categories.value = (d?.data ?? d ?? []).filter(Boolean);
  } catch {
    /* ignore */
  } finally {
    loadingCats.value = false;
  }
}
watch(
  organizationIdCommerce,
  (v) => {
    if (v) fetchCategories();
  },
  { immediate: true },
);

const selectedCats = ref<string[]>([]);
const originalCats = ref<string[]>([]);
function toggleCat(id: string) {
  const i = selectedCats.value.indexOf(id);
  if (i >= 0) selectedCats.value.splice(i, 1);
  else selectedCats.value.push(id);
}

/* Formulaire inline catégorie */
const showCatForm = ref(false);
const editCatId = ref<string | null>(null);
const catFormLang = ref<'fr' | 'en' | 'nl'>('fr');
const catNameFr = ref('');
const catNameEn = ref('');
const catNameNl = ref('');
const catDescFr = ref('');
const catDescEn = ref('');
const catDescNl = ref('');
const catModules = ref<string[]>([]);
const catSaving = ref(false);
const catError = ref<string | null>(null);
const attemptedSaveCat = ref(false);
const showCatNameFrError = computed(
  () => attemptedSaveCat.value && !catNameFr.value.trim(),
);
const transNameCat = ref(false);
const transDescCat = ref(false);
const deletingCatId = ref<string | null>(null);

function openNewCat() {
  editCatId.value = null;
  catFormLang.value = 'fr';
  catNameFr.value = catNameEn.value = catNameNl.value = '';
  catDescFr.value = catDescEn.value = catDescNl.value = '';
  catModules.value = [];
  catError.value = null;
  attemptedSaveCat.value = false;
  showCatForm.value = true;
}

async function fillMissingCatTranslations(
  values: Record<'fr' | 'en' | 'nl', string>,
  setters: Record<'fr' | 'en' | 'nl', (v: string) => void>,
  busy: { value: boolean },
  stillCurrent: () => boolean,
) {
  const source = (['fr', 'en', 'nl'] as const).find((l) => values[l].trim());
  if (!source) return;
  const missing = (['fr', 'en', 'nl'] as const).filter(
    (l) => l !== source && !values[l].trim(),
  );
  if (!missing.length) return;
  busy.value = true;
  try {
    const translated = await TranslationService.translateText(
      values[source],
      source,
    );
    if (!stillCurrent()) return;
    missing.forEach((l) => setters[l](translated[l]));
  } finally {
    if (stillCurrent()) busy.value = false;
  }
}

async function openEditCat(c: Category) {
  editCatId.value = c.id;
  const d = c.designation;
  catNameFr.value = typeof d === 'string' ? d : (d?.fr ?? '');
  catNameEn.value = typeof d === 'string' ? '' : (d?.en ?? '');
  catNameNl.value = typeof d === 'string' ? '' : (d?.nl ?? '');
  const desc = c.description;
  catDescFr.value = typeof desc === 'string' ? desc : (desc?.fr ?? '');
  catDescEn.value = typeof desc === 'string' ? '' : (desc?.en ?? '');
  catDescNl.value = typeof desc === 'string' ? '' : (desc?.nl ?? '');
  catModules.value = Array.isArray(c.modules) ? [...c.modules] : [];
  catFormLang.value = 'fr';
  catError.value = null;
  attemptedSaveCat.value = false;
  showCatForm.value = true;

  const thisCatId = c.id;
  const stillCurrent = () => editCatId.value === thisCatId;
  await fillMissingCatTranslations(
    { fr: catNameFr.value, en: catNameEn.value, nl: catNameNl.value },
    {
      fr: (v) => (catNameFr.value = v),
      en: (v) => (catNameEn.value = v),
      nl: (v) => (catNameNl.value = v),
    },
    transNameCat,
    stillCurrent,
  );
  await fillMissingCatTranslations(
    { fr: catDescFr.value, en: catDescEn.value, nl: catDescNl.value },
    {
      fr: (v) => (catDescFr.value = v),
      en: (v) => (catDescEn.value = v),
      nl: (v) => (catDescNl.value = v),
    },
    transDescCat,
    stillCurrent,
  );
}
function closeCatForm() {
  showCatForm.value = false;
  editCatId.value = null;
}

function onCatNameBlur(lang: 'fr' | 'en' | 'nl') {
  if (catFormLang.value !== lang) return;
  const g: Record<string, () => string> = {
    fr: () => catNameFr.value,
    en: () => catNameEn.value,
    nl: () => catNameNl.value,
  };
  translateAll(
    lang,
    g[lang],
    (l, v) => {
      if (l === 'fr') catNameFr.value = v;
      if (l === 'en') catNameEn.value = v;
      if (l === 'nl') catNameNl.value = v;
    },
    transNameCat,
  );
}
function onCatDescBlur(lang: 'fr' | 'en' | 'nl') {
  if (catFormLang.value !== lang) return;
  const g: Record<string, () => string> = {
    fr: () => catDescFr.value,
    en: () => catDescEn.value,
    nl: () => catDescNl.value,
  };
  translateAll(
    lang,
    g[lang],
    (l, v) => {
      if (l === 'fr') catDescFr.value = v;
      if (l === 'en') catDescEn.value = v;
      if (l === 'nl') catDescNl.value = v;
    },
    transDescCat,
  );
}

async function saveCat() {
  attemptedSaveCat.value = true;
  if (!catNameFr.value.trim()) {
    return;
  }
  catSaving.value = true;
  catError.value = null;
  try {
    const payload: any = {
      designation: {
        fr: catNameFr.value.trim(),
        en: catNameEn.value.trim(),
        nl: catNameNl.value.trim(),
      },
      description: {
        fr: catDescFr.value.trim(),
        en: catDescEn.value.trim(),
        nl: catDescNl.value.trim(),
      },
      modules: catModules.value,
    };
    if (editCatId.value) {
      if (organizationIdCommerce.value)
        payload.organizationId = organizationIdCommerce.value;
      await axiosInstance.put(
        `products/categories/update/${editCatId.value}`,
        payload,
      );
    } else {
      if (fromId.value) payload.restaurantId = fromId.value;
      const r = await axiosInstance.post('products/categories/create', payload);
      const created = (r.data as any)?.data ?? (r.data as any);
      if (created?.id) categories.value.push(created);
    }
    await fetchCategories();
    closeCatForm();
  } catch (err: any) {
    const raw = err?.response?.data?.message;
    catError.value = Array.isArray(raw)
      ? raw[0]
      : (raw ?? err?.message ?? 'Erreur');
  } finally {
    catSaving.value = false;
  }
}

async function deleteCat(cat: Category) {
  if (deletingCatId.value) return;
  deletingCatId.value = cat.id;
  try {
    await axiosInstance.delete(`products/categories/delete/${cat.id}`);
    selectedCats.value = selectedCats.value.filter((cid) => cid !== cat.id);
    await fetchCategories();
  } catch {
    /* ignore */
  } finally {
    deletingCatId.value = null;
  }
}

/* ── Désignation ────────────────────────────────────────────────────────── */
const nameLang = ref<'fr' | 'en' | 'nl'>('fr');
const nameFr = ref('');
const nameEn = ref('');
const nameNl = ref('');
const transName = ref(false);
function onNameBlur(lang: 'fr' | 'en' | 'nl') {
  if (nameLang.value !== lang) return;
  const g: Record<string, () => string> = {
    fr: () => nameFr.value,
    en: () => nameEn.value,
    nl: () => nameNl.value,
  };
  translateAll(
    lang,
    g[lang],
    (l, v) => {
      if (l === 'fr') nameFr.value = v;
      if (l === 'en') nameEn.value = v;
      if (l === 'nl') nameNl.value = v;
    },
    transName,
  );
}

/* ── Description ────────────────────────────────────────────────────────── */
const descLang = ref<'fr' | 'en' | 'nl'>('fr');
const descFr = ref('');
const descEn = ref('');
const descNl = ref('');
const transDesc = ref(false);
function onDescBlur(lang: 'fr' | 'en' | 'nl') {
  if (descLang.value !== lang) return;
  const g: Record<string, () => string> = {
    fr: () => descFr.value,
    en: () => descEn.value,
    nl: () => descNl.value,
  };
  translateAll(
    lang,
    g[lang],
    (l, v) => {
      if (l === 'fr') descFr.value = v;
      if (l === 'en') descEn.value = v;
      if (l === 'nl') descNl.value = v;
    },
    transDesc,
  );
}

/* ── Prix & type ────────────────────────────────────────────────────────── */
const PRODUCT_TYPES = computed(() => [
  { value: 'food', label: t('product.typeFood') },
  { value: 'extra', label: t('product.typeExtra') },
  { value: 'food&extra', label: t('product.typeFoodExtra') },
]);

const price = ref('');
const productType = ref('food');
const priceUnit = ref('PER_PIECE');
const packageQty = ref('');
const packageUnit = ref('');

/* ── Disponibilité ──────────────────────────────────────────────────────── */
const availableAt = ref<string | null>(null);
const preparationTime = ref<number | null>(null);

/* ── TVA ────────────────────────────────────────────────────────────────── */
interface TaxRate {
  id: string;
  name: string;
  rate: number;
  country: string;
}
const taxRates = ref<TaxRate[]>([]);
const loadingTaxRates = ref(false);
const tvaId = ref('');
const tvaDropdownOpen = ref(false);
const tvaSearch = ref('');

async function fetchTaxRates() {
  if (!fromId.value) return;
  loadingTaxRates.value = true;
  try {
    const params = new URLSearchParams({
      pagination: 'false',
      pageSize: '200',
      storeId: fromId.value,
    });
    const r = await axiosInstance.get(`taxes/search?${params}`);
    const d = r.data as any;
    taxRates.value = d?.data ?? [];
  } catch {
    taxRates.value = [];
  } finally {
    loadingTaxRates.value = false;
  }
}
watch(
  fromId,
  (v) => {
    if (v) fetchTaxRates();
  },
  { immediate: true },
);

const filteredTaxRates = computed(() =>
  taxRates.value.filter((tx) =>
    tx.name.toLowerCase().includes(tvaSearch.value.trim().toLowerCase()),
  ),
);
const selectedTaxRate = computed(() =>
  taxRates.value.find((tx) => tx.id === tvaId.value),
);
function selectTvaId(id: string) {
  tvaId.value = id;
  tvaDropdownOpen.value = false;
  tvaSearch.value = '';
}

const PRICE_UNIT_VALUES = ['PER_PIECE', 'PER_KG', 'PER_100G', 'PER_LITER'];
const PRICE_UNIT_OPTIONS = computed(() => [
  { value: 'PER_PIECE', label: t('product.perPiece') },
  { value: 'PER_KG', label: t('product.perKg') },
  { value: 'PER_100G', label: t('product.per100g') },
  { value: 'PER_LITER', label: t('product.perLiter') },
]);
const PACKAGE_UNIT_VALUES = ['UNIT', 'PACK', 'BATCH', 'BOX', 'BUNDLE'];
const PACKAGE_UNIT_OPTIONS = computed(() => [
  { value: '', label: t('product.packageUnitNone') },
  { value: 'UNIT', label: t('product.packageUnitUnit') },
  { value: 'PACK', label: t('product.packageUnitPack') },
  { value: 'BATCH', label: t('product.packageUnitBatch') },
  { value: 'BOX', label: t('product.packageUnitBox') },
  { value: 'BUNDLE', label: t('product.packageUnitBundle') },
]);

// Le backend peut renvoyer des valeurs hors enum (ex: "pièce" au lieu de
// PER_PIECE) sur des données historiques — le select n'a alors aucune option
// correspondante mais la valeur invalide reste en mémoire et casse le PUT.
function normalizePriceUnit(v: unknown): string {
  return typeof v === 'string' && PRICE_UNIT_VALUES.includes(v)
    ? v
    : 'PER_PIECE';
}
function normalizePackageUnit(v: unknown): string {
  return typeof v === 'string' && PACKAGE_UNIT_VALUES.includes(v) ? v : '';
}

/* ── Image ──────────────────────────────────────────────────────────────── */
const imgFile = ref<File | null>(null);
const imgPreview = ref<string | null>(null);
const existingImgUrl = ref<string | null>(null);
const imgInputRef = ref<HTMLInputElement | null>(null);
const imgUploading = ref(false);

function onImgChange(e: Event) {
  const f = (e.target as HTMLInputElement).files?.[0] ?? null;
  if (!f) return;
  imgFile.value = f;
  const reader = new FileReader();
  reader.onload = (ev) => {
    imgPreview.value = ev.target?.result as string;
  };
  reader.readAsDataURL(f);
}
function removeImg() {
  imgFile.value = null;
  imgPreview.value = null;
  if (imgInputRef.value) imgInputRef.value.value = '';
}

async function uploadImageIfNeeded(): Promise<string | null> {
  if (!imgFile.value) return null;
  imgUploading.value = true;
  try {
    const fd = new FormData();
    fd.append('file', imgFile.value);
    fd.append('designation', nameFr.value.trim() || 'product-image');
    const r = await axios.post(`${VITE_API_URL}files/upload`, fd, {
      headers: { Authorization: `Bearer ${token.value?.accessToken}` },
    });
    const d = r.data as any;
    return d?.data?.id ?? d?.id ?? null;
  } finally {
    imgUploading.value = false;
  }
}

/* ── Variantes (déclaré ici pour être disponible dans le watch) ─────────── */
type Variant = {
  _key: string;
  id?: string;
  labelFr: string;
  labelEn: string;
  labelNl: string;
  labelLang: 'fr' | 'en' | 'nl';
  price: string;
  priceUnit: string;
  packageQty: string;
  packageUnit: string;
  isDefault: boolean;
  status: 'active' | 'inactive' | 'out-of-stock';
  translating: boolean;
};

let variantKeySeq = 0;
function nextVariantKey() {
  variantKeySeq += 1;
  return `v${variantKeySeq}`;
}

const variants = ref<Variant[]>([]);
const deletedVariants = ref<string[]>([]);

/* ── AddonGroups (déclaré ici pour être disponible dans le watch) ───────── */
type AddonOption = {
  id?: string;
  contentFr: string;
  contentEn: string;
  contentNl: string;
  optLang: 'fr' | 'en' | 'nl';
  price: string;
  translating: boolean;
};
type AddonGroup = {
  id?: string;
  titleFr: string;
  titleEn: string;
  titleNl: string;
  titleLang: 'fr' | 'en' | 'nl';
  extraType: 'side-dish' | 'supplements' | 'proposal';
  chooseType: 'multiple' | 'unique';
  required: boolean;
  minQty: string;
  maxQty: string;
  options: AddonOption[];
  translating: boolean;
};

const addonGroups = ref<AddonGroup[]>([]);

/* ── Auto-traduction des champs FR-only au chargement ───────────────────── */
async function fillMissing(
  fr: string,
  en: string,
  nl: string,
  setEn: (v: string) => void,
  setNl: (v: string) => void,
) {
  if (!fr.trim()) return;
  const needEn = !en.trim();
  const needNl = !nl.trim();
  if (!needEn && !needNl) return;
  const translated = await TranslationService.translateText(fr, 'fr');
  if (needEn) setEn(translated.en);
  if (needNl) setNl(translated.nl);
}

async function autoTranslateMissing() {
  await fillMissing(
    nameFr.value,
    nameEn.value,
    nameNl.value,
    (v) => (nameEn.value = v),
    (v) => (nameNl.value = v),
  );
  await fillMissing(
    descFr.value,
    descEn.value,
    descNl.value,
    (v) => (descEn.value = v),
    (v) => (descNl.value = v),
  );
  await Promise.all(
    variants.value.map((v) =>
      fillMissing(
        v.labelFr,
        v.labelEn,
        v.labelNl,
        (val) => (v.labelEn = val),
        (val) => (v.labelNl = val),
      ),
    ),
  );
}

async function autoTranslateMissingAddonGroups() {
  await Promise.all(
    addonGroups.value.map(async (g) => {
      await fillMissing(
        g.titleFr,
        g.titleEn,
        g.titleNl,
        (val) => (g.titleEn = val),
        (val) => (g.titleNl = val),
      );
      await Promise.all(
        g.options.map((o) =>
          fillMissing(
            o.contentFr,
            o.contentEn,
            o.contentNl,
            (val) => (o.contentEn = val),
            (val) => (o.contentNl = val),
          ),
        ),
      );
    }),
  );
}

/* ── Pré-remplissage depuis les données API ─────────────────────────────── */
const initialized = ref(false);
const initialSnapshot = ref<string | null>(null);

function buildSnapshot() {
  return JSON.stringify({
    nameFr: nameFr.value,
    nameEn: nameEn.value,
    nameNl: nameNl.value,
    descFr: descFr.value,
    descEn: descEn.value,
    descNl: descNl.value,
    price: price.value,
    productType: productType.value,
    priceUnit: priceUnit.value,
    packageQty: packageQty.value,
    packageUnit: packageUnit.value,
    tvaId: tvaId.value,
    availableAt: availableAt.value,
    preparationTime: preparationTime.value,
    selectedCats: [...selectedCats.value].sort(),
    hasNewImage: !!imgFile.value,
    variants: variants.value.map((v) => ({
      id: v.id ?? null,
      labelFr: v.labelFr,
      labelEn: v.labelEn,
      labelNl: v.labelNl,
      price: v.price,
      priceUnit: v.priceUnit,
      packageQty: v.packageQty,
      packageUnit: v.packageUnit,
      isDefault: v.isDefault,
      status: v.status,
    })),
    deletedVariants: [...deletedVariants.value].sort(),
  });
}

const hasChanges = computed(() => {
  if (initialSnapshot.value === null) return false;
  return buildSnapshot() !== initialSnapshot.value;
});

watch(
  product,
  (p) => {
    if (!p || initialized.value) return;
    initialized.value = true;

    // Désignation — le backend renvoie soit un objet {fr,en,nl}, soit une
    // string simple accompagnée des traductions dans designationAlternatives.
    const des = p.designation;
    const desAlt = p.designationAlternatives;
    if (desAlt) {
      nameFr.value = desAlt.fr ?? (typeof des === 'string' ? des : '');
      nameEn.value = desAlt.en ?? '';
      nameNl.value = desAlt.nl ?? '';
    } else if (des && typeof des === 'object') {
      nameFr.value = des.fr ?? '';
      nameEn.value = des.en ?? '';
      nameNl.value = des.nl ?? '';
    } else if (typeof des === 'string') {
      nameFr.value = des;
    }

    // Description — même logique que la désignation ci-dessus.
    const desc = p.description;
    const descAlt = p.descriptionAlternatives;
    if (descAlt) {
      descFr.value = descAlt.fr ?? (typeof desc === 'string' ? desc : '');
      descEn.value = descAlt.en ?? '';
      descNl.value = descAlt.nl ?? '';
    } else if (desc && typeof desc === 'object') {
      descFr.value = desc.fr ?? '';
      descEn.value = desc.en ?? '';
      descNl.value = desc.nl ?? '';
    } else if (typeof desc === 'string') {
      descFr.value = desc;
    }

    // Prix, type, unités
    price.value = String(p.price ?? '');
    productType.value = p.type ?? 'food';
    priceUnit.value = normalizePriceUnit(p.priceUnit);
    packageQty.value = p.packageQty != null ? String(p.packageQty) : '';
    packageUnit.value = normalizePackageUnit(p.packageUnit);

    // TVA
    tvaId.value = p.taxes?.[0]?.taxId ?? '';

    // Disponibilité
    availableAt.value = p.avalaibleAt ?? null;
    preparationTime.value = p.preparationTime ?? null;

    // Image existante
    existingImgUrl.value = p.image?.info?.secureUrl ?? p.image?.url ?? null;

    // Catégories sélectionnées
    const cats = p.categories ?? [];
    selectedCats.value = cats
      .map((c: any) => c?.categoryId ?? c?.category?.id ?? c?.id)
      .filter(Boolean);
    originalCats.value = [...selectedCats.value];

    // Sync org ID
    const orgId = p.organizationId ?? p.organization?.id ?? null;
    if (orgId && !organizationIdCommerce.value)
      organizationIdCommerce.value = orgId;

    // Variantes existantes
    const rawVariants = p.variants ?? p.productVariants ?? [];
    variants.value = rawVariants.map((v: any) => {
      const lbl = v.label ?? {};
      const lblAlt = v.labelAlternatives;
      return {
        _key: nextVariantKey(),
        id: v.id,
        labelFr: lblAlt?.fr ?? (typeof lbl === 'string' ? lbl : (lbl.fr ?? '')),
        labelEn: lblAlt?.en ?? (typeof lbl === 'string' ? '' : (lbl.en ?? '')),
        labelNl: lblAlt?.nl ?? (typeof lbl === 'string' ? '' : (lbl.nl ?? '')),
        labelLang: 'fr' as const,
        price: String(v.price ?? ''),
        priceUnit: normalizePriceUnit(v.priceUnit),
        packageQty: v.packageQty != null ? String(v.packageQty) : '',
        packageUnit: normalizePackageUnit(v.packageUnit),
        isDefault: v.isDefault ?? false,
        status: v.status ?? 'active',
        translating: false,
      };
    });

    // AddonGroups existants (extras) — voir watch(productExtras, ...)
    // ci-dessous, product/get/{id} ne les contient pas.

    autoTranslateMissing().then(() => {
      nextTick(() => {
        initialSnapshot.value = buildSnapshot();
      });
    });
  },
  { immediate: true },
);

function mapRawExtrasToAddonGroups(rawExtras: any[]): AddonGroup[] {
  return rawExtras.map((g: any) => {
    const title = g.title ?? g.designation ?? {};
    const rawOpts = g.option ?? g.options ?? g.extraOptions ?? [];
    return {
      id: g.id,
      titleFr: typeof title === 'string' ? title : (title.fr ?? ''),
      titleEn: typeof title === 'string' ? '' : (title.en ?? ''),
      titleNl: typeof title === 'string' ? '' : (title.nl ?? ''),
      titleLang: 'fr' as const,
      extraType: g.extraType ?? 'supplements',
      chooseType: g.chooseType ?? 'multiple',
      required: g.required ?? false,
      minQty: String(g.minQty ?? 0),
      maxQty: String(g.maxQty ?? 1),
      translating: false,
      options: rawOpts.length
        ? rawOpts.map((o: any) => {
            const content = o.content ?? o.designation ?? {};
            return {
              id: o.id,
              contentFr:
                typeof content === 'string' ? content : (content.fr ?? ''),
              contentEn: typeof content === 'string' ? '' : (content.en ?? ''),
              contentNl: typeof content === 'string' ? '' : (content.nl ?? ''),
              optLang: 'fr' as const,
              price: String(o.price ?? ''),
              translating: false,
            };
          })
        : [
            {
              contentFr: '',
              contentEn: '',
              contentNl: '',
              optLang: 'fr' as const,
              price: '',
              translating: false,
            },
          ],
    };
  });
}

// Ré-exécuté après un attach/detach immédiat (hors du flux batch de
// sauvegarde du produit) : refetch direct plutôt qu'une invalidation, car le
// watch ci-dessous n'agit qu'une fois (addonGroupsInitialized) et ignorerait
// un nouveau résultat de la query `product-extras`.
async function refetchAddonGroups() {
  const r = await axiosInstance.get(`product/${id.value}/extras`);
  const d = r.data as any;
  const rawExtras = (d?.data ?? d ?? []) as any[];
  addonGroups.value = mapRawExtrasToAddonGroups(rawExtras);
}

const addonGroupsInitialized = ref(false);
watch(
  productExtras,
  (rawExtras) => {
    if (!rawExtras || addonGroupsInitialized.value) return;
    addonGroupsInitialized.value = true;
    addonGroups.value = mapRawExtrasToAddonGroups(rawExtras);

    autoTranslateMissingAddonGroups().then(() => {
      nextTick(() => {
        if (initialized.value) initialSnapshot.value = buildSnapshot();
      });
    });
  },
  { immediate: true },
);

/* ── Soumission ─────────────────────────────────────────────────────────── */
const saving = ref(false);
const error = ref<string | null>(null);
const done = ref(false);

// N'affiche les erreurs de champ (bordure rouge + message) qu'après une
// première tentative de soumission, pas dès l'ouverture du formulaire.
const attemptedSubmit = ref(false);
const showNameFrError = computed(
  () => attemptedSubmit.value && !nameFr.value.trim(),
);
const showNameEnError = computed(
  () => attemptedSubmit.value && !nameEn.value.trim(),
);
const showNameNlError = computed(
  () => attemptedSubmit.value && !nameNl.value.trim(),
);
const showPriceError = computed(
  () =>
    attemptedSubmit.value && (!price.value || isNaN(parseFloat(price.value))),
);

// Une variante "remplie" (au moins un libellé saisi) doit avoir un prix
// valide — sinon parseFloat(...) || 0 l'enverrait silencieusement à 0€.
function isVariantFilled(v: Variant) {
  return !!(v.labelFr.trim() || v.labelEn.trim() || v.labelNl.trim());
}
function isVariantPriceInvalid(v: Variant) {
  // v-model sur <input type="number"> peut renvoyer un number malgré le
  // typage Variant.price: string, donc on normalise avant de valider.
  const raw = String(v.price ?? '').trim();
  return !raw || isNaN(parseFloat(raw));
}
const invalidVariantIndexes = computed(() =>
  variants.value
    .map((v, i) => (isVariantFilled(v) && isVariantPriceInvalid(v) ? i : -1))
    .filter((i) => i !== -1),
);
function showVariantPriceError(i: number) {
  return attemptedSubmit.value && invalidVariantIndexes.value.includes(i);
}

watch(id, () => {
  initialized.value = false;
  addonGroupsInitialized.value = false;
  initialSnapshot.value = null;
  done.value = false;
  error.value = null;
  imgFile.value = null;
  imgPreview.value = null;
  deletedVariants.value = [];
});

async function submit() {
  attemptedSubmit.value = true;
  if (!nameFr.value.trim()) {
    error.value = 'La désignation (FR) est obligatoire';
    return;
  }
  if (!nameEn.value.trim()) {
    error.value = 'La désignation (EN) est obligatoire';
    return;
  }
  if (!nameNl.value.trim()) {
    error.value = 'La désignation (NL) est obligatoire';
    return;
  }
  if (!price.value || isNaN(parseFloat(price.value))) {
    error.value = 'Le prix est obligatoire';
    return;
  }
  if (invalidVariantIndexes.value.length) {
    error.value = `Le prix de la variante ${invalidVariantIndexes.value[0] + 1} est invalide`;
    return;
  }

  saving.value = true;
  error.value = null;
  try {
    /* 1. Upload nouvelle image si changée */
    const newImageId = await uploadImageIfNeeded();

    /* 2. PUT products/update/{id} */
    const addedCats = selectedCats.value.filter(
      (cid) => !originalCats.value.includes(cid),
    );
    const removedCats = originalCats.value.filter(
      (cid) => !selectedCats.value.includes(cid),
    );
    const body: any = {
      designation: {
        fr: nameFr.value.trim(),
        en: nameEn.value.trim(),
        nl: nameNl.value.trim(),
      },
      price: parseFloat(price.value),
      type: productType.value,
      priceUnit: priceUnit.value,
      needExtra: addonGroups.value.length > 0,
    };
    if (addedCats.length) body.addInCategories = addedCats;
    if (removedCats.length) body.removeFromCategories = removedCats;
    if (newImageId) body.imageId = newImageId;
    if (descFr.value.trim() || descEn.value.trim() || descNl.value.trim()) {
      body.description = {
        fr: descFr.value,
        en: descEn.value,
        nl: descNl.value,
      };
    }
    if (packageQty.value) body.packageQty = parseInt(packageQty.value);
    if (packageUnit.value) body.packageUnit = packageUnit.value;
    if (tvaId.value) body.tvaId = tvaId.value;
    body.avalaibleAt = availableAt.value || undefined;
    body.preparationTime = preparationTime.value || undefined;

    await axiosInstance.put(`products/update/${id.value}`, body);

    /* 3. Variantes — DELETE supprimées */
    for (const vid of deletedVariants.value) {
      await axiosInstance
        .delete(`products/${id.value}/variants/${vid}`)
        .catch(() => {});
    }

    /* 3b. Variantes existantes — PUT (isDefault non accepté par cette route) */
    for (const v of variants.value.filter((v) => !!v.id)) {
      const vBody: any = {
        label: {
          fr: v.labelFr.trim(),
          en: v.labelEn.trim(),
          nl: v.labelNl.trim(),
        },
        price: parseFloat(v.price) || 0,
        priceUnit: v.priceUnit,
        status: v.status,
      };
      if (v.packageQty) vBody.packageQty = parseInt(v.packageQty);
      if (v.packageUnit) vBody.packageUnit = v.packageUnit;
      await axiosInstance.put(`products/${id.value}/variants/${v.id}`, vBody);
    }

    /* 3c. Nouvelles variantes — POST bulk */
    const newVariants = variants.value.filter(
      (v) =>
        !v.id && (v.labelFr.trim() || v.labelEn.trim() || v.labelNl.trim()),
    );
    if (newVariants.length) {
      await axiosInstance.post(
        `products/${id.value}/variants/create-variant-bulk`,
        {
          variants: newVariants.map((v) => {
            const vb: any = {
              label: {
                fr: v.labelFr.trim(),
                en: v.labelEn.trim(),
                nl: v.labelNl.trim(),
              },
              price: parseFloat(v.price) || 0,
              priceUnit: v.priceUnit,
              status: v.status,
              isDefault: v.isDefault,
              optionValueIds: [],
            };
            if (v.packageQty) vb.packageQty = parseInt(v.packageQty);
            if (v.packageUnit) vb.packageUnit = v.packageUnit;
            if (fromId.value) vb.storeId = [fromId.value];
            return vb;
          }),
        },
      );
    }

    // Les groupes d'options ne sont plus créés/modifiés/supprimés depuis
    // cette page : attach/detach est immédiat (voir attachGroup/detachGroup
    // ci-dessous), et le CRUD complet vit dans CommerceDetailPage.vue
    // (Catalogue > Groupes d'options).

    queryClient.invalidateQueries({
      queryKey: ['product-detail', id.value],
      refetchType: 'all',
    });
    // Les catégories du produit affectent le catalogue et l'assignation de
    // son commerce d'origine : sans ça, revenir sur CommerceDetailPage.vue
    // après avoir changé les collections d'un produit montre encore
    // l'ancienne répartition tant qu'on ne force pas un rechargement.
    if (fromId.value) {
      queryClient.invalidateQueries({ queryKey: ['commerce-products'] });
      queryClient.invalidateQueries({ queryKey: ['commerce-products-all'] });
      queryClient.invalidateQueries({
        queryKey: ['commerce-products-assign-all', fromId.value],
      });
      queryClient.invalidateQueries({
        queryKey: ['commerce-products-for-opts', fromId.value],
      });
      queryClient.invalidateQueries({
        queryKey: ['commerce-org-categories', fromId.value],
      });
    }
    done.value = true;
  } catch (err: any) {
    const raw = err?.response?.data?.message;
    error.value = Array.isArray(raw)
      ? raw[0]
      : (raw ?? err?.message ?? 'Une erreur est survenue');
  } finally {
    saving.value = false;
  }
}

/* ── Variantes — fonctions ──────────────────────────────────────────────── */

function addVariant() {
  variants.value.push({
    _key: nextVariantKey(),
    labelFr: '',
    labelEn: '',
    labelNl: '',
    labelLang: 'fr',
    price: '',
    priceUnit: 'PER_PIECE',
    packageQty: '',
    packageUnit: '',
    isDefault: false,
    status: 'active',
    translating: false,
  });
}
function removeVariant(i: number) {
  const v = variants.value[i];
  if (v.id) deletedVariants.value.push(v.id);
  variants.value.splice(i, 1);
}

function setDefaultVariant(i: number) {
  const target = variants.value[i];
  if (!target.isDefault) return;
  variants.value.forEach((v, vi) => {
    if (vi !== i) v.isDefault = false;
  });
}

function onVariantLabelBlur(i: number, lang: 'fr' | 'en' | 'nl') {
  const v = variants.value[i];
  if (v.labelLang !== lang) return;
  const g: Record<string, () => string> = {
    fr: () => v.labelFr,
    en: () => v.labelEn,
    nl: () => v.labelNl,
  };
  const busy = {
    get value() {
      return v.translating;
    },
    set value(x: boolean) {
      v.translating = x;
    },
  };
  translateAll(
    lang,
    g[lang],
    (l, val) => {
      if (l === 'fr') v.labelFr = val;
      if (l === 'en') v.labelEn = val;
      if (l === 'nl') v.labelNl = val;
    },
    busy,
  );
}

// ── Détacher / attacher un groupe existant ──────────────────────────────
// Le CRUD complet (créer/modifier/supprimer un groupe) vit dans
// CommerceDetailPage.vue (Catalogue > Groupes d'options) ; cette page ne
// gère plus que le lien attach/detach entre un groupe et CE produit.
// Actions immédiates (hors flux batch de sauvegarde) : POST/DELETE
// extras/{groupId}/products/{productId} pour le lien, GET extras/search
// (excludeProductId déjà géré côté serveur) pour le picker — un seul appel,
// plus le Promise.all par produit du commerce utilisé avant que cet
// endpoint de recherche ne soit confirmé disponible.
const isDetachingGroupId = ref<string | null>(null);
async function detachGroup(g: AddonGroup) {
  if (!g.id) return;
  isDetachingGroupId.value = g.id;
  try {
    await axiosInstance.delete(`extras/${g.id}/products/${id.value}`);
    await refetchAddonGroups();
  } finally {
    isDetachingGroupId.value = null;
  }
}

const isAttachPickerOpen = ref(false);
const isLoadingAttachableGroups = ref(false);
const attachableGroups = ref<{ id: string; label: string }[]>([]);
const attachingGroupId = ref<string | null>(null);
const attachPickerError = ref('');
const attachSearch = ref('');

async function searchAttachableGroups() {
  if (!fromId.value || !id.value) return;
  isLoadingAttachableGroups.value = true;
  attachPickerError.value = '';
  try {
    const params = new URLSearchParams();
    params.append('storeId', fromId.value);
    params.append('excludeProductId', id.value);
    params.append('pagination', 'true');
    params.append('pageSize', '50');
    if (attachSearch.value.trim())
      params.append('q', attachSearch.value.trim());

    const r = await axiosInstance.get(`extras/search?${params.toString()}`);
    const d = r.data as any;
    const groups = (d?.data ?? []) as any[];
    attachableGroups.value = groups
      .filter((g) => !!g.id)
      .map((g) => {
        const title = g.title ?? g.designation ?? {};
        const label = (typeof title === 'string' ? title : title.fr) || g.id;
        return { id: g.id, label };
      });
  } catch {
    attachPickerError.value = t('product.attachGroupLoadError');
  } finally {
    isLoadingAttachableGroups.value = false;
  }
}
function openAttachPicker() {
  isAttachPickerOpen.value = true;
  attachSearch.value = '';
  searchAttachableGroups();
}
function closeAttachPicker() {
  isAttachPickerOpen.value = false;
}

let attachSearchDebounce: ReturnType<typeof setTimeout> | undefined;
watch(attachSearch, () => {
  if (!isAttachPickerOpen.value) return;
  clearTimeout(attachSearchDebounce);
  attachSearchDebounce = setTimeout(searchAttachableGroups, 300);
});

async function attachGroup(groupId: string) {
  attachingGroupId.value = groupId;
  attachPickerError.value = '';
  try {
    await axiosInstance.post(`extras/${groupId}/products/${id.value}`);
    await refetchAddonGroups();
    isAttachPickerOpen.value = false;
  } catch {
    attachPickerError.value = t('product.attachGroupError');
  } finally {
    attachingGroupId.value = null;
  }
}
/* ── Image affichée dans la preview ────────────────────────────────────── */
const displayImg = computed(() => imgPreview.value ?? existingImgUrl.value);

// ProductPricingTab/ProductAddonGroupsTab reçoivent des Ref<T> bruts pour
// muter directement le parent (pas de v-model). Un ref référencé par son
// identifiant dans CE template est auto-déballé par Vue au niveau du
// compilateur (même passé en argument de fonction), donc `:price="price"`
// enverrait la string et non le Ref lui-même. Ces getters ferment sur le
// ref sans jamais l'exposer comme identifiant nu dans le template.
function getPriceRef() {
  return price;
}
function getProductTypeRef() {
  return productType;
}
function getPriceUnitRef() {
  return priceUnit;
}
function getPackageQtyRef() {
  return packageQty;
}
function getPackageUnitRef() {
  return packageUnit;
}
function getTvaIdRef() {
  return tvaId;
}
function getTvaDropdownOpenRef() {
  return tvaDropdownOpen;
}
function getTvaSearchRef() {
  return tvaSearch;
}
function getAddonGroupsRef() {
  return addonGroups;
}
function getAvailableAtRef() {
  return availableAt;
}
function getPreparationTimeRef() {
  return preparationTime;
}
</script>

<template>
  <div class="epp" @click="tvaDropdownOpen = false">
    <!-- Breadcrumb -->
    <nav class="epp__bc">
      <button class="epp__bc-link" @click="goBack()">
        <i class="pi pi-arrow-left" />
        {{ fromId ? t('product.backToCommerce') : t('product.backToProduct') }}
      </button>
      <template v-if="fromId">
        <i class="pi pi-angle-right epp__bc-sep" />
        <span class="epp__bc-cur">{{ t('product.editProduct') }}</span>
      </template>
      <template v-if="fromId && storeProducts && storeProducts.length > 1">
        <div class="epp__pagination">
          <button
            class="epp__page-btn"
            :disabled="!prevProduct"
            :title="
              prevProduct?.designation?.fr || prevProduct?.designation || ''
            "
            @click="prevProduct && goToProduct(prevProduct.id)"
          >
            <i class="pi pi-chevron-left" />
          </button>
          <span class="epp__page-counter">
            {{ currentIndex + 1 }} / {{ storeProducts.length }}
          </span>
          <button
            class="epp__page-btn"
            :disabled="!nextProduct"
            :title="
              nextProduct?.designation?.fr || nextProduct?.designation || ''
            "
            @click="nextProduct && goToProduct(nextProduct.id)"
          >
            <i class="pi pi-chevron-right" />
          </button>
        </div>
      </template>
    </nav>

    <!-- Skeleton -->
    <template v-if="loadingProduct">
      <div class="epp__skeleton">
        <div class="epp__sk epp__sk--hd" />
        <div class="epp__sk epp__sk--body" />
      </div>
    </template>

    <!-- Erreur -->
    <div v-else-if="loadError" class="epp__load-error">
      <i class="pi pi-exclamation-circle" />
      <p>{{ t('product.loadError') }}</p>
      <button class="epp__btn epp__btn--ghost" @click="router.back()">
        <i class="pi pi-arrow-left" /> {{ t('product.back') }}
      </button>
    </div>

    <!-- Succès -->
    <div v-else-if="done" class="epp__success">
      <div class="epp__success-icon"><i class="pi pi-check" /></div>
      <h2 class="epp__success-title">{{ t('product.productUpdated') }}</h2>
      <p class="epp__success-sub">{{ t('product.productUpdatedDesc') }}</p>
      <div class="epp__success-actions">
        <button
          class="epp__btn epp__btn--ghost"
          @click="
            router.push(`/products/${id}${fromId ? '?from=' + fromId : ''}`)
          "
        >
          <i class="pi pi-eye" /> {{ t('product.viewProduct') }}
        </button>
      </div>
    </div>

    <template v-else-if="product">
      <!-- Header -->
      <div class="epp__header">
        <div class="epp__header-icon"><i class="pi pi-pencil" /></div>
        <div>
          <h1 class="epp__title">{{ t('product.editProductTitle') }}</h1>
          <p class="epp__sub">
            {{ nameFr || product?.designation?.fr || t('product.typeFood') }}
          </p>
        </div>
      </div>

      <div class="epp__body">
        <ProductEditTabs v-model="activeTab" :tabs="TAB_LIST" />

        <div class="epp__tab-panel">
          <ProductGeneralTab
            v-if="activeTab === 'general'"
            :product-id="id"
            :display-img="displayImg"
            :existing-img-url="existingImgUrl"
            :img-file="imgFile"
            :attempted-submit="attemptedSubmit"
            :trans-name="transName"
            :trans-desc="transDesc"
            v-model:name-lang="nameLang"
            v-model:name-fr="nameFr"
            v-model:name-en="nameEn"
            v-model:name-nl="nameNl"
            v-model:desc-lang="descLang"
            v-model:desc-fr="descFr"
            v-model:desc-en="descEn"
            v-model:desc-nl="descNl"
            :categories="categories"
            :loading-cats="loadingCats"
            :selected-cats="selectedCats"
            :show-cat-form="showCatForm"
            :edit-cat-id="editCatId"
            v-model:cat-form-lang="catFormLang"
            v-model:cat-name-fr="catNameFr"
            v-model:cat-name-en="catNameEn"
            v-model:cat-name-nl="catNameNl"
            v-model:cat-desc-fr="catDescFr"
            v-model:cat-desc-en="catDescEn"
            v-model:cat-desc-nl="catDescNl"
            v-model:cat-modules="catModules"
            :cat-saving="catSaving"
            :cat-error="catError"
            :trans-name-cat="transNameCat"
            :trans-desc-cat="transDescCat"
            :deleting-cat-id="deletingCatId"
            :cat-label="catLabel"
            @img-change="onImgChange"
            @img-remove="removeImg"
            @name-blur="onNameBlur"
            @desc-blur="onDescBlur"
            @open-new-cat="openNewCat"
            @open-edit-cat="openEditCat"
            @close-cat-form="closeCatForm"
            @save-cat="saveCat"
            @delete-cat="deleteCat"
            @toggle-cat="toggleCat"
            @cat-name-blur="onCatNameBlur"
            @cat-desc-blur="onCatDescBlur"
          />

          <ProductPricingTab
            v-else-if="activeTab === 'pricing'"
            :price="getPriceRef()"
            :product-type="getProductTypeRef()"
            :price-unit="getPriceUnitRef()"
            :package-qty="getPackageQtyRef()"
            :package-unit="getPackageUnitRef()"
            :tva-id="getTvaIdRef()"
            :tva-dropdown-open="getTvaDropdownOpenRef()"
            :tva-search="getTvaSearchRef()"
            :tax-rates="taxRates"
            :loading-tax-rates="loadingTaxRates"
            :filtered-tax-rates="filteredTaxRates"
            :selected-tax-rate="selectedTaxRate"
            :show-price-error="showPriceError"
            v-bind="{
              PRICE_UNIT_OPTIONS,
              PACKAGE_UNIT_OPTIONS,
              PRODUCT_TYPES,
            }"
            :select-tva-id="selectTvaId"
          />

          <ProductVariantsTab
            v-else-if="activeTab === 'variants'"
            :product-id="id"
            :variants="variants"
            :price-unit-options="PRICE_UNIT_OPTIONS"
            :package-unit-options="PACKAGE_UNIT_OPTIONS"
            :add-variant="addVariant"
            :remove-variant="removeVariant"
            :set-default-variant="setDefaultVariant"
            :on-variant-label-blur="onVariantLabelBlur"
            :show-variant-price-error="showVariantPriceError"
          />

          <ProductAddonGroupsTab
            v-else-if="activeTab === 'addonGroups'"
            :addon-groups="getAddonGroupsRef()"
            :on-open-attach-picker="openAttachPicker"
            :on-detach-group="detachGroup"
            :is-detaching-group-id="isDetachingGroupId"
            :can-attach-existing="!!fromId"
          />

          <ProductAllergensTab
            v-else-if="activeTab === 'allergens'"
            :product-id="id"
            :variants="variants"
            :addon-groups="addonGroups"
          />

          <ProductAvailabilityTab
            v-else-if="activeTab === 'availability'"
            :available-at="getAvailableAtRef()"
            :preparation-time="getPreparationTimeRef()"
          />
        </div>

        <!-- Erreur -->
        <div v-if="error" class="epp__error-banner">
          <i class="pi pi-exclamation-circle" /> {{ error }}
        </div>

        <!-- Actions -->
        <div class="epp__actions">
          <button class="epp__btn epp__btn--ghost" @click="goBack()">
            <i class="pi pi-times" /> {{ t('product.cancel') }}
          </button>
          <button
            class="epp__btn epp__btn--primary"
            :disabled="saving || imgUploading || !hasChanges"
            :title="!hasChanges ? t('product.noChangesHint') : undefined"
            @click="submit"
          >
            <span
              v-if="saving || imgUploading"
              class="epp__spinner epp__spinner--white"
            />
            <i v-else class="pi pi-check" />
            {{
              imgUploading
                ? t('product.uploadingImage')
                : saving
                  ? t('product.saving')
                  : t('product.saveChanges')
            }}
          </button>
        </div>
      </div>
    </template>

    <!-- Picker "attacher un groupe existant" — extras/search, scopé à ce
         commerce et excluant les groupes déjà attachés à ce produit
         (excludeProductId géré côté serveur). -->
    <div
      v-if="isAttachPickerOpen"
      class="epp__attach-overlay"
      @click.self="closeAttachPicker"
    >
      <div class="epp__attach-modal">
        <div class="epp__attach-head">
          <h3>{{ t('product.attachGroupTitle') }}</h3>
          <button class="epp__icon-btn" @click="closeAttachPicker">
            <i class="pi pi-times" />
          </button>
        </div>

        <p v-if="!fromId" class="epp__attach-hint">
          {{ t('product.attachGroupNoCommerceHint') }}
        </p>
        <template v-else>
          <input
            v-model="attachSearch"
            type="text"
            :placeholder="t('product.attachGroupSearchPlaceholder')"
            class="epp__input mb-3 w-full"
          />

          <div v-if="isLoadingAttachableGroups" class="epp__attach-loading">
            <i class="pi pi-spin pi-spinner" />
            {{ t('product.attachGroupLoading') }}
          </div>
          <p v-else-if="attachPickerError" class="epp__attach-error">
            {{ attachPickerError }}
          </p>
          <p v-else-if="!attachableGroups.length" class="epp__attach-hint">
            {{ t('product.attachGroupEmpty') }}
          </p>
          <ul v-else class="epp__attach-list">
            <li
              v-for="g in attachableGroups"
              :key="g.id"
              class="epp__attach-item"
            >
              <span class="epp__attach-item-label">{{ g.label }}</span>
              <button
                class="epp__add-btn epp__add-btn--sm"
                :disabled="attachingGroupId === g.id"
                @click="attachGroup(g.id)"
              >
                <i
                  :class="
                    attachingGroupId === g.id
                      ? 'pi pi-spin pi-spinner'
                      : 'pi pi-link'
                  "
                />
                {{ t('product.attachGroupAction') }}
              </button>
            </li>
          </ul>
        </template>
      </div>
    </div>
  </div>
</template>

<style scoped>
.epp {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  min-width: 0;
  padding: 2.5rem 3rem;
  box-sizing: border-box;
}

/* Picker "attacher un groupe existant" */
.epp__attach-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
  padding: 1rem;
}
.epp__attach-modal {
  background: var(--nxt-card-bg);
  border-radius: 1rem;
  padding: 1.5rem;
  width: 100%;
  max-width: 28rem;
  max-height: 80vh;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
.epp__attach-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.epp__attach-head h3 {
  margin: 0;
  font-size: 1rem;
  font-weight: 700;
}
.epp__attach-hint,
.epp__attach-error {
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-muted);
}
.epp__attach-error {
  color: #ba1a1a;
}
.epp__attach-loading {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-muted);
}
.epp__attach-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
}
.epp__attach-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.75rem;
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 0.625rem;
}
.epp__attach-item-info {
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  min-width: 0;
}
.epp__attach-item-label {
  font-size: 0.875rem;
  font-weight: 700;
}
.epp__attach-item-sub {
  font-size: 0.6875rem;
  color: var(--nxt-on-surface-muted);
}
[data-theme='dark'] .epp__attach-modal {
  background: rgba(20, 28, 41, 0.98);
}
[data-theme='dark'] .epp__attach-item {
  border-color: rgba(255, 255, 255, 0.08);
}

/* Breadcrumb */
.epp__bc {
  display: flex;
  align-items: center;
  gap: 0.4rem;
}

/* Pagination */
.epp__pagination {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 0.25rem;
}
.epp__page-btn {
  background: var(--nxt-surface-low);
  border: 1.5px solid var(--nxt-outline-variant);
  border-radius: 0.5rem;
  width: 2rem;
  height: 2rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: var(--nxt-on-surface-muted);
  font-size: 0.75rem;
  transition:
    background 0.15s,
    color 0.15s,
    border-color 0.15s;
}
.epp__page-btn:not(:disabled):hover {
  background: var(--nxt-primary);
  border-color: var(--nxt-primary);
  color: #fff;
}
.epp__page-btn:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}
.epp__page-counter {
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--nxt-on-surface-muted);
  min-width: 3rem;
  text-align: center;
}
.epp__bc-link {
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-size: 0.8125rem;
  font-family: inherit;
  color: var(--nxt-on-surface-muted);
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  transition: color 0.15s;
}
.epp__bc-link:hover {
  color: var(--nxt-primary);
}
.epp__bc-sep {
  font-size: 0.75rem;
  color: var(--nxt-on-surface-muted);
}
.epp__bc-cur {
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-muted);
}

/* Skeleton */
.epp__skeleton {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
.epp__sk {
  border-radius: 1rem;
  background: linear-gradient(
    90deg,
    var(--nxt-surface-low) 25%,
    var(--nxt-surface-mid) 50%,
    var(--nxt-surface-low) 75%
  );
  background-size: 200% 100%;
  animation: epp-sk 1.4s infinite;
}
.epp__sk--hd {
  height: 5rem;
}
.epp__sk--body {
  height: 24rem;
}
@keyframes epp-sk {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

/* Load error */
.epp__load-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  padding: 3rem;
  text-align: center;
  background: var(--nxt-card-bg);
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 1.25rem;
}
.epp__load-error i {
  font-size: 2rem;
  color: var(--nxt-error);
}
.epp__load-error p {
  margin: 0;
  color: var(--nxt-on-surface-muted);
}

/* Success */
.epp__success {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  padding: 3rem;
  text-align: center;
  background: var(--nxt-card-bg);
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 1.25rem;
}
.epp__success-icon {
  width: 4rem;
  height: 4rem;
  border-radius: 50%;
  background: rgba(16, 181, 16, 0.1);
  border: 1px solid rgba(16, 181, 16, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.75rem;
  color: #10b510;
}
.epp__success-title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 800;
  color: var(--nxt-on-surface);
}
.epp__success-sub {
  margin: 0;
  font-size: 0.875rem;
  color: var(--nxt-on-surface-muted);
}
.epp__success-actions {
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
  justify-content: center;
}

/* Header */
.epp__header {
  display: flex;
  align-items: center;
  gap: 1rem;
}
.epp__header-icon {
  width: 3rem;
  height: 3rem;
  border-radius: 0.875rem;
  background: rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(0, 0, 0, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  color: var(--nxt-primary);
  flex-shrink: 0;
}
.epp__title {
  margin: 0;
  font-size: 1.375rem;
  font-weight: 900;
  letter-spacing: -0.025em;
  color: var(--nxt-on-surface);
}
.epp__sub {
  margin: 0.2rem 0 0;
  font-size: 0.875rem;
  color: var(--nxt-on-surface-muted);
}

/* Body / tab shell */
.epp__body {
  display: flex;
  flex-direction: column;
  gap: 0;
  background: var(--nxt-card-bg);
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 1.25rem;
  overflow: hidden;
  box-shadow: var(--nxt-shadow-sm);
}
.epp__tab-panel {
  display: flex;
  flex-direction: column;
}
@media (max-width: 1023px) {
  .epp {
    padding: 1.5rem;
  }
}
@media (max-width: 639px) {
  .epp {
    padding: 1rem;
  }
}

/* Error */
.epp__error-banner {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  padding: 0.875rem 1.125rem;
  border-radius: 0.75rem;
  background: rgba(239, 68, 68, 0.07);
  border: 1px solid rgba(239, 68, 68, 0.2);
  color: #ef4444;
  font-size: 0.875rem;
  font-weight: 600;
  margin: 0 1.5rem;
}

/* Actions */
.epp__actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1.5rem;
  border-top: 1px solid var(--nxt-outline-variant);
  background: var(--nxt-surface-low);
  flex-wrap: wrap;
}

/* Buttons */
.epp__btn {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  height: 2.5rem;
  padding: 0 1.25rem;
  border-radius: 0.625rem;
  font-size: 0.875rem;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  white-space: nowrap;
  border: none;
  transition:
    filter 0.15s,
    opacity 0.15s;
  flex-shrink: 0;
}
.epp__btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
.epp__btn--primary {
  background: var(--nxt-gradient-primary);
  color: var(--nxt-on-primary);
  box-shadow: var(--nxt-gradient-primary-btn-shadow);
}
.epp__btn--primary:not(:disabled):hover {
  filter: brightness(1.07);
}
.epp__btn--ghost {
  background: var(--nxt-surface-low);
  color: var(--nxt-on-surface-variant);
  border: 1px solid var(--nxt-outline-variant);
}
.epp__btn--ghost:hover {
  background: var(--nxt-surface-mid);
  color: var(--nxt-on-surface);
}
.epp__btn--outline {
  background: var(--nxt-card-bg);
  color: var(--nxt-on-surface);
  border: 1px solid var(--nxt-outline-variant);
}
.epp__btn--outline:hover {
  background: var(--nxt-surface-low);
}
.epp__btn--danger {
  background: rgba(239, 68, 68, 0.08);
  color: #ef4444;
  border: 1px solid rgba(239, 68, 68, 0.25);
}
.epp__btn--danger:not(:disabled):hover {
  background: rgba(239, 68, 68, 0.15);
}

/* Spinner */
.epp__spinner {
  width: 0.9rem;
  height: 0.9rem;
  border: 2px solid currentColor;
  border-top-color: transparent;
  border-radius: 50%;
  animation: epp-spin 0.7s linear infinite;
  display: inline-block;
}
.epp__spinner--white {
  border-color: rgba(255, 255, 255, 0.4);
  border-top-color: #fff;
}
@keyframes epp-spin {
  to {
    transform: rotate(360deg);
  }
}
</style>

<style>
/* ── Dark mode overrides (page chrome only — section-specific dark-mode
   rules moved into their respective tab components) ─────────────────── */
[data-theme='dark'] .epp {
  background: #0c1421;
}

[data-theme='dark'] .epp__body,
[data-theme='dark'] .epp__load-error,
[data-theme='dark'] .epp__success {
  background: rgba(20, 28, 41, 0.8);
  border-color: rgba(255, 255, 255, 0.08);
}

[data-theme='dark'] .epp__actions {
  background: rgba(255, 255, 255, 0.03);
  border-top-color: rgba(255, 255, 255, 0.08);
}

/* Header icon */
[data-theme='dark'] .epp__header-icon {
  background: rgba(255, 255, 255, 0.06);
  border-color: rgba(255, 255, 255, 0.08);
  color: #ffd60a;
}

/* Pagination */
[data-theme='dark'] .epp__page-btn {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.1);
}
[data-theme='dark'] .epp__page-btn:not(:disabled):hover {
  background: #ffd60a;
  border-color: #ffd60a;
  color: #0b1320;
}
[data-theme='dark'] .epp__bc-link:hover {
  color: #ffd60a;
}

/* Buttons */
[data-theme='dark'] .epp__btn--primary {
  background: #ffd60a;
  color: #0b1320;
  box-shadow: 0 2px 8px rgba(255, 214, 10, 0.3);
}
[data-theme='dark'] .epp__btn--ghost {
  background: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.6);
  border-color: rgba(255, 255, 255, 0.1);
}
[data-theme='dark'] .epp__btn--ghost:hover {
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
}
[data-theme='dark'] .epp__btn--outline {
  background: rgba(255, 255, 255, 0.05);
  color: #fff;
  border-color: rgba(255, 255, 255, 0.1);
}
[data-theme='dark'] .epp__btn--outline:hover {
  background: rgba(255, 255, 255, 0.1);
}

/* Skeleton */
[data-theme='dark'] .epp__sk {
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0.04) 25%,
    rgba(255, 255, 255, 0.08) 50%,
    rgba(255, 255, 255, 0.04) 75%
  );
  background-size: 200% 100%;
}
</style>
